import React, { useContext, useEffect, useRef, useState } from 'react';

import { useStyles } from './styles';
import {
  TextField,
  Typography,
  Grid,
  TableContainer,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import { useParams } from 'react-router-dom';
import TableL7Resources from './TableL7Resources';
import TableScroll from '../../../components/global/table/TableScroll';
import { PageContext } from '../../../context/pages';
import { toast } from 'react-toastify';
import {
  GET_L7_RESOURCES,
  GET_PARTNER_CLIENT_ACCOUNT,
  GET_ACCOUNT_SETTINGS,
} from '../../../actions/get';
import { UPDATE_ACCOUNT_SETTINGS_L7 } from '../../../actions/update';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useDebouncedEffect } from '../../../hooks/useDebouncedEffect';
import Preloader from '../../../components/global/Preloader';
import TablePaginationComponent from '../../../components/global/table/TablePagination';
import ModalSwitchOffResource from '../../../components/modals/modalsL7Protection/switchOffResource';
import ModalSwitchOnResource from '../../../components/modals/modalsL7Protection/switchOnResource';

const L7ProtectionAccount = ({ dataPartnerClientAccount }) => {
  const classes = useStyles();
  const table = useRef(null);
  const [switches, setSwitches] = useState(false);
  const [l7visibility, setL7visibility] = useState(null);

  const [trialPeriod, setTrialPeriod] = useState(new Date());
  const [switchTrialPeriod, setSwitchTrialPeriod] = useState(false);

  const { id: partnerClientAccountId } = useParams();

  const { filter, setFilter } = useContext(PageContext);
  const [sortActive, setSortActive] = useState('sortL7ResourceId');

  const [isSwitchOn, setIsSwitchOn] = useState({
    switchOn: false,
    l7ResourceId: '',
    name: '',
  });
  const [isSwitchOff, setIsSwitchOff] = useState({
    switchOff: false,
    l7ResourceId: '',
    name: '',
  });

  // получение partnerClientAccount
  const { data: partnerClientAccount, loading: loadingPartnerClientAccount } = useQuery(
    GET_PARTNER_CLIENT_ACCOUNT,
    {
      variables: {
        id: Number(partnerClientAccountId),
      },
    }
  );

  // получение accountSettingsId из partnerClientAccount
  const accountSettingsId =
    partnerClientAccount && partnerClientAccount?.partnerClientAccount?.accountSettings?.id;

  const [getDataSettingApi, { data: dataSetting }] = useLazyQuery(GET_ACCOUNT_SETTINGS, {
    variables: {
      id: partnerClientAccount && partnerClientAccount.partnerClientAccount.accountSettings.id,
    },
  });

  useEffect(() => {
    if (accountSettingsId) {
      getDataSettingApi();
    }
  }, [accountSettingsId]);

  // мутации связанные с updateAccountSettingsL7
  const [updateAccountSettingsL7Api] = useMutation(UPDATE_ACCOUNT_SETTINGS_L7, {
    onCompleted: () => toast.success('Success'),
    refetchQueries: [
      {
        query: GET_ACCOUNT_SETTINGS,
        variables: {
          id: accountSettingsId,
        },
      },
    ],
  });

  const onChangeVisibleL7Service = (value) => {
    setL7visibility(value);
    updateL7Visibility(value);
  };

  const updateL7Limit = (value) => {
    updateAccountSettingsL7Api({
      variables: {
        id: accountSettingsId,
        l7Limit: Number(value),
        visibility: l7visibility,
      },
    });
  };

  const [getFilter, { data, loading }] = useLazyQuery(GET_L7_RESOURCES);
  const getVariables = () => {
    const proxy = {
      limit: filter.limit,
      page: filter.page + 1,
      l7ResourceName: filter?.l7ResourceName || undefined,
      serviceIp: filter?.serviceIp || undefined,
      deletedAt: null,
      partnerClientAccountId: Number(partnerClientAccountId),
      [sortActive]: filter?.sort?.[sortActive]?.toUpperCase() || 'DESC',
    };

    if (filter?.l7ResourceId) proxy.l7ResourceId = Number(filter?.l7ResourceId);
    return proxy;
  };

  useDebouncedEffect(
    () => {
      getFilter({ variables: getVariables() });
    },
    500,
    [filter]
  );

  useEffect(() => {
    if (dataPartnerClientAccount && dataPartnerClientAccount.accountSettings.l7.l7Limit >= 1) {
      setSwitches(true);
    } else {
      setSwitches(false);
    }
  }, [dataPartnerClientAccount]);

  const updateL7Visibility = (value) => {
    updateAccountSettingsL7Api({
      variables: {
        id: accountSettingsId,
        l7Limit: dataSetting?.accountSettings?.l7?.l7Limit,
        visibility: value,
      },
    });
  };

  const handleChangeTrialDatePicker = (date) => {
    setTrialPeriod(date);

    updateAccountSettingsL7Api({
      variables: {
        id: accountSettingsId,
        trialPeriodL7: date,
        l7Limit: dataSetting.accountSettings.l7.l7Limit,
        visibility: l7visibility,
      },
    });
  };

  const handleChangeTrialSwitch = (event) => {
    setSwitchTrialPeriod(event.target.checked);

    if (!event.target.checked) {
      setTrialPeriod(new Date());
      updateAccountSettingsL7Api({
        variables: {
          id: accountSettingsId,
          trialPeriodL7: null,
          l7Limit: dataSetting?.accountSettings?.l7?.l7Limit,
          visibility: l7visibility,
        },
      });
    } else {
      updateAccountSettingsL7Api({
        variables: {
          id: accountSettingsId,
          trialPeriodL7: trialPeriod,
          l7Limit: dataSetting?.accountSettings?.l7?.l7Limit,
          visibility: l7visibility,
        },
      });
    }
  };

  // обновление стейт параметров
  useEffect(() => {
    if (dataSetting && dataSetting.accountSettings?.l7) {
      const { analyzer, l7Incidents, l7Limit, trialPeriod, l7Events, visibility } =
        dataSetting.accountSettings.l7;

      setSwitchTrialPeriod(Boolean(trialPeriod));
      setL7visibility(visibility);
      trialPeriod && setTrialPeriod(new Date(trialPeriod));
    }
  }, [dataSetting]);

  if (loading) return <Preloader />;

  return (
    <>
      <ModalSwitchOffResource
        show={isSwitchOff.switchOff}
        hideModal={() => setIsSwitchOff(!isSwitchOff.switchOff)}
        l7ResourceId={isSwitchOff.l7ResourceId}
        name={isSwitchOff.name}
        partnerClientAccountId={isSwitchOff.partnerClientAccountId}
        query={GET_L7_RESOURCES}
        getVariables={getVariables}
      />
      <ModalSwitchOnResource
        show={isSwitchOn.switchOn}
        hideModal={() => setIsSwitchOn(!isSwitchOn.switchOn)}
        l7ResourceId={isSwitchOn.l7ResourceId}
        name={isSwitchOn.name}
        partnerClientAccountId={isSwitchOn.partnerClientAccountId}
        query={GET_L7_RESOURCES}
        getVariables={getVariables}
      />

      <div className={classes.radioWrap}>
        <FormControl>
          <div style={{ fontSize: '20px', marginBottom: '15px' }}>
            Service visibility in client dashboard:
          </div>
          <RadioGroup
            row
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue="female"
            name="radio-buttons-group"
            value={l7visibility}
            onChange={(event) => {
              onChangeVisibleL7Service(event.target.value);
            }}
          >
            <FormControlLabel value="INVISIBLE" control={<Radio />} label="INVISIBLE" />
            <FormControlLabel value="DEMO" control={<Radio />} label="DEMO" />
            <FormControlLabel value="VISIBLE" control={<Radio />} label="VISIBLE" />
          </RadioGroup>
        </FormControl>
      </div>

      <div className={classes.wrapperGroup}>
        <div className={classes.pickerWrapper}>
          <div className={classes.switchWrapper}>
            <Switch name="trial" checked={switchTrialPeriod} onChange={handleChangeTrialSwitch} />
          </div>

          <Typography className={classes.text}>Trial period till:</Typography>

          <div style={{ position: 'relative' }}>
            <DatePicker
              onChange={handleChangeTrialDatePicker}
              selected={trialPeriod}
              disabled={!switchTrialPeriod}
              className={classes.dataPicker}
              dateFormat="dd.MM.yyyy"
              popperClassName={classes.poper}
              showPopperArrow={false}
            />
          </div>
        </div>
      </div>

      <Typography variant="h6" style={{ marginBottom: 15, marginTop: '15px' }}>
        Resource limit:
      </Typography>

      <Grid container>
        <Grid item xs>
          <div className={classes.inputWrapper}>
            {dataPartnerClientAccount && (
              <TextField
                defaultValue=""
                placeholder={
                  dataPartnerClientAccount.accountSettings.l7.l7Limit === null
                    ? 0
                    : dataPartnerClientAccount.accountSettings.l7.l7Limit
                }
                disabled={!switches}
                onBlur={(event) => {
                  if (event.target.value !== '') {
                    updateL7Limit(event.target.value);
                  }
                }}
                variant="outlined"
                className={classes.input}
              />
            )}
          </div>
        </Grid>
      </Grid>
      <div>
        <Typography variant="h6" style={{ marginBottom: 15, marginTop: '15px' }}>
          Application protection resource list
        </Typography>

        <TableScroll tableRef={table}>
          <TableContainer>
            <TableL7Resources
              data={data}
              setFilter={setFilter}
              filter={filter}
              setIsSwitchOff={setIsSwitchOff}
              setIsSwitchOn={setIsSwitchOn}
              sortActive={sortActive}
              setSortActive={setSortActive}
              partnerClientAccountId={partnerClientAccountId}
            />
            <TablePaginationComponent
              filter={filter}
              setFilter={setFilter}
              count={data && data.l7Resources.info.totalCount}
              getFilter={getFilter}
              getVariables={getVariables()}
              type={'FILTER_ACTIVE'}
            />
          </TableContainer>
        </TableScroll>
      </div>
    </>
  );
};
export default L7ProtectionAccount;
